import { AuthService } from '../../auth/auth.service';
import { GlobalStationSearchService } from './global-station-search.service';

import { CommonModule } from '@angular/common';
import { Component, inject, input, output, signal } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import {
  PossibleSourceIds,
  possibleSourceIds,
} from './models/possibleSourceIdsForStations';
import { normalizeStationSearch, StationSearch } from './models/station-search';
import { distinctUntilChanged, Observable, of, startWith } from 'rxjs';
import { debounceTime, finalize, map, switchMap, tap } from 'rxjs/operators';

export const MODULES = [
  CommonModule,
  MatButtonModule,
  MatCardModule,
  MatIconModule,
  ReactiveFormsModule,
  MatFormFieldModule,
  MatInputModule,
  MatAutocompleteModule,
  MatProgressSpinnerModule,
];
export const COMPONENTS = [FaIconComponent];

@Component({
  selector: 'app-global-station-search-bar',
  templateUrl: './global-station-search-bar.component.html',
  styleUrls: ['./global-station-search-bar.component.scss'],
  imports: [...MODULES, ...COMPONENTS, MatTooltip],
})
export class GlobalStationSearchBarComponent {
  private fb = inject(UntypedFormBuilder);
  private gs = inject(GlobalStationSearchService);
  public auth = inject(AuthService);

  globalSearchForm: UntypedFormGroup;
  filteredStations$: Observable<StationSearch[]>;

  showGlobalSearch = signal(false);
  isStationLoading = signal(false);
  noResults = signal(false);

  selectedStationValue = signal<StationSearch | null>(null);

  sourceIdNotToInclude = input<PossibleSourceIds>(null);
  shouldTranslatedSourceIdBeAdded = input(false);

  selectedStation = output<StationSearch>();

  onShowGlobalSearch(): void {
    this.showGlobalSearch.update(v => !v);

    if (this.showGlobalSearch() && !this.globalSearchForm) {
      this.initGlobalSearchForm();
    }

    if (this.showGlobalSearch() && this.selectedStationValue()) {
      this.globalSearchForm
        ?.get('stationInput')
        ?.setValue(this.displayStation(this.selectedStationValue()), {
          emitEvent: false,
        });
    }
  }

  initGlobalSearchForm(): void {
    this.globalSearchForm = this.fb.group({
      stationInput: [''],
    });

    const stationInputControl = this.globalSearchForm.get('stationInput');

    this.filteredStations$ = stationInputControl.valueChanges.pipe(
      startWith(''),
      map(value => (typeof value === 'string' ? value.trim() : '')),
      distinctUntilChanged(),
      debounceTime(300),
      tap(() => {
        this.isStationLoading.set(true);
        this.noResults.set(false);
      }),
      switchMap(searchTerm => {
        if (searchTerm.length < 3) {
          this.isStationLoading.set(false);
          this.noResults.set(false);
          return of([]);
        }

        return this.gs.searchStation({ name: searchTerm }).pipe(
          map(stations => this.filterStations(stations, searchTerm)),
          tap(stations => this.noResults.set(stations.length === 0)),
          finalize(() => this.isStationLoading.set(false))
        );
      })
    );
  }

  displayStation(station: StationSearch | string | null): string {
    if (!station) {
      return '';
    }

    if (typeof station === 'string') {
      return station;
    }

    const elevationPart =
      station.elevation != null ? ` | ${station.elevation} m` : '';
    return `${station.overwriteStationName ?? station.name}${elevationPart}`;
  }

  onStationChange(event: MatOptionSelectionChange, selectedStation): void {
    if (!event.isUserInput || !selectedStation) {
      return;
    }

    this.showGlobalSearch.set(false);

    const normalized = normalizeStationSearch(selectedStation);
    if (!normalized) {
      return;
    }

    if (this.shouldTranslatedSourceIdBeAdded()) {
      normalized.nameOfSource = this.translateSourceIdIntoRealName(
        normalized.source_id
      );
    }

    this.selectedStationValue.set(normalized);

    this.globalSearchForm
      ?.get('stationInput')
      ?.setValue(this.displayStation(normalized), { emitEvent: false });

    this.selectedStation.emit(normalized);
  }

  private filterStations(
    stations: StationSearch[],
    searchTerm: string
  ): StationSearch[] {
    const filtered = this.filterOutStationsWithCertainSourceId(stations);
    const normalizedTerm = searchTerm.toLowerCase();

    return filtered.filter(station =>
      (station.name ?? '').toLowerCase().includes(normalizedTerm)
    );
  }

  filterOutStationsWithCertainSourceId(
    stations: StationSearch[]
  ): StationSearch[] {
    const sourceIdNotToInclude = this.sourceIdNotToInclude();

    if (
      sourceIdNotToInclude == null ||
      typeof sourceIdNotToInclude !== 'number' ||
      !Array.isArray(stations)
    ) {
      return stations ?? [];
    }

    return stations.filter(
      singleStation => singleStation.source_id !== sourceIdNotToInclude
    );
  }

  translateSourceIdIntoRealName(sourceId: number): string {
    const sourceIdString = String(sourceId);
    return sourceId != null && possibleSourceIds.hasOwnProperty(sourceIdString)
      ? possibleSourceIds[sourceIdString]
      : 'sourceId not defined';
  }
}
